// Issue Card Component
// Date: January 17, 2025
// Purpose: Card view for Issue entities

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarIcon,
  UserIcon,
  ShieldCheckIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useLanguage } from '@/state/language/LanguageProvider';

export interface IssueCardProps {
  issue: {
    id: string;
    title?: string;
    description?: string;
    status?: string;
    urgency?: string;
    impact?: string;
    category_name?: string;
    creator_name?: string;
    assigned_to_name?: string;
    target_type?: string;
    privacy?: string;
    created_at?: string;
    resolved_at?: string;
    [key: string]: any;
  };
  onView?: (item: any) => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  onCopy?: (item: any) => void;
  onClick?: (id: string) => void;
  canEdit?: (item: any) => boolean;
  customActions?: Array<{
    label: string | ((item: any) => string);
    icon: React.ComponentType<any> | ((item: any) => React.ComponentType<any>);
    onClick: (item: any) => void | Promise<void>;
    variant?: 'primary' | 'secondary' | 'danger' | ((item: any) => 'primary' | 'secondary' | 'danger');
  }>;
}

export const IssueCard: React.FC<IssueCardProps> = ({
  issue,
  onView,
  onEdit,
  onDelete,
  onCopy,
  onClick,
  canEdit,
  customActions = []
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleClick = () => {
    if (onClick) {
      onClick(issue.id);
    } else if (onView) {
      onView(issue);
    } else {
      navigate(`/issues/${issue.id}`);
    }
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  const getUrgencyColor = (urgency?: string) => {
    switch (urgency?.toUpperCase()) {
      case 'CRITICAL':
        return 'bg-[var(--color-error)]/20 text-[var(--color-error)] border-[var(--color-error)]/30';
      case 'HIGH':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'MEDIUM':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'LOW':
        return 'bg-[var(--color-success)]/20 text-[var(--color-success)] border-[var(--color-success)]/30';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
    }
  };

  const getImpactColor = (impact?: string) => {
    switch (impact?.toUpperCase()) {
      case 'CRITICAL':
        return 'bg-[var(--color-error)]/20 text-[var(--color-error)] border-[var(--color-error)]/30';
      case 'HIGH':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'MEDIUM':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'LOW':
        return 'bg-[var(--color-success)]/20 text-[var(--color-success)] border-[var(--color-success)]/30';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'OPEN':
        return 'bg-[var(--color-info)]/20 text-[var(--color-info)] border-[var(--color-info)]/30';
      case 'IN_PROGRESS':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'RESOLVED':
        return 'bg-[var(--color-success)]/20 text-[var(--color-success)] border-[var(--color-success)]/30';
      case 'CLOSED':
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
    }
  };

  const getPrivacyIcon = (privacy?: string) => {
    if (privacy?.toUpperCase() === 'PRIVATE') {
      return <ShieldCheckIcon className="w-4 h-4 text-gray-500" />;
    }
    return null;
  };

  return (
    <Card
      variant="outline"
      padding="none"
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)]"
      onClick={handleClick}
    >
      <CardContent className="p-4 flex-1 flex flex-col min-h-0">
        {/* Top: status */}
        {issue.status && (
          <div className="mb-2">
            <Badge className={`text-xs border ${getStatusColor(issue.status)}`}>
              {issue.status.replace('_', ' ')}
            </Badge>
          </div>
        )}
        {/* Title + actions */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-semibold text-[var(--color-secondary-text)] text-base line-clamp-1 flex-1 min-w-0">
            {issue.title || 'Issue'}
          </h3>
          <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {onView && (
              <button
                onClick={(e) => handleActionClick(e, () => onView(issue))}
                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title={t('common.view')}
                type="button"
              >
                <EyeIcon className="w-4 h-4" />
              </button>
            )}
            {onEdit && (canEdit ? canEdit(issue) : true) && (
              <button
                onClick={(e) => handleActionClick(e, () => onEdit(issue))}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                title={t('common.edit')}
                type="button"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            )}
            {onCopy && (
              <button
                onClick={(e) => handleActionClick(e, () => onCopy(issue))}
                className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                title={t('common.copy')}
                type="button"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => handleActionClick(e, () => onDelete(issue))}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title={t('common.delete')}
                type="button"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
            {customActions.map((action, index) => {
              const IconComponent = typeof action.icon === 'function' && action.icon.length > 0
                ? (action.icon as (item: any) => React.ComponentType<any>)(issue)
                : action.icon as React.ComponentType<any>;
              const variant = typeof action.variant === 'function'
                ? action.variant(issue)
                : action.variant || 'primary';
              const colorClass = variant === 'danger'
                ? 'text-red-600 hover:bg-red-50'
                : variant === 'secondary'
                  ? 'text-gray-600 hover:bg-gray-50'
                  : 'text-blue-600 hover:bg-blue-50';
              return (
                <button
                  key={index}
                  onClick={(e) => handleActionClick(e, () => action.onClick(issue))}
                  className={`p-1.5 ${colorClass} rounded-md transition-colors`}
                  title={typeof action.label === 'function' ? action.label(issue) : action.label}
                  type="button"
                >
                  <IconComponent className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Description: prominent block with more space */}
        <div className="flex-1 min-h-[4.5rem] mb-3">
          {issue.description ? (
            <p className="text-sm text-[var(--color-secondary-text)] leading-relaxed line-clamp-5 break-words">
              {issue.description}
            </p>
          ) : (
            <p className="text-sm text-[var(--color-secondary-text)] opacity-60 italic">No description</p>
          )}
        </div>

        {/* Bottom: category, urgency, impact (importance/urgency) */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {issue.category_name && (
            <Badge variant="ghost" className="text-xs">
              {issue.category_name}
            </Badge>
          )}
          {issue.urgency && (
            <Badge className={`text-xs border ${getUrgencyColor(issue.urgency)}`}>
              {issue.urgency}
            </Badge>
          )}
          {issue.impact && (
            <Badge className={`text-xs border ${getImpactColor(issue.impact)}`}>
              Impact: {issue.impact}
            </Badge>
          )}
        </div>

        {/* Metadata footer */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--color-secondary-text)] opacity-80 border-t border-[var(--color-border)] pt-2 mt-auto">
          {issue.creator_name && (
            <span className="flex items-center gap-1">
              <UserIcon className="w-3.5 h-3.5" />
              Raised by {issue.creator_name}
            </span>
          )}
          {issue.assigned_to_name && (
            <span className="flex items-center gap-1">
              <UserIcon className="w-3.5 h-3.5" />
              Assigned to {issue.assigned_to_name}
            </span>
          )}
          {issue.target_type && <span>Target: {issue.target_type}</span>}
          {issue.privacy && (
            <span className="flex items-center gap-1">
              {getPrivacyIcon(issue.privacy)}
              {issue.privacy}
            </span>
          )}
          {issue.created_at && (
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5" />
              {new Date(issue.created_at).toLocaleDateString()}
            </span>
          )}
          {issue.resolved_at && (
            <span className="flex items-center gap-1 text-green-600">
              <CalendarIcon className="w-3.5 h-3.5" />
              Resolved {new Date(issue.resolved_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

